/**
 * Version precedence, in TypeScript (KIR-5042).
 *
 * The TWIN of `bestVersionExpr` / `versionSortKeyExpr` in
 * apps/backend/src/db/repositories/mcp-sql-expressions.ts, and it exists
 * because the same pick has to be made in three places that cannot share SQL:
 * the grouped list query (SQL), the per-user drilldown (which aggregates in
 * TypeScript, not in the database), and the Version cell (which re-ranks what
 * it was given). Three hand-rolled comparators would drift, and the symptom
 * would be a cell and a popup disagreeing about one user's version.
 *
 * Keep this file and `mcp-sql-expressions.ts` in step. Both are exercised
 * against the same ordering table — here in `mcp-version.test.ts`, and on real
 * Postgres in `org-mcp-version-column.test.ts`.
 */
/**
 * A comparable key for one version string, ordering versions NATURALLY rather
 * than lexically.
 *
 * Mirrors the SQL key exactly: a leading `v` is dropped, build metadata
 * (`+...`) is dropped — semver ignores it, so `1.2.3+b5` and `1.2.3` are one
 * version — and every digit run is left-padded to ten characters so numeric
 * segments compare by magnitude (otherwise `0.10.0` sorts below `0.9.2`).
 *
 * A PyPI tag with no hyphen (`1.2.3rc1`, `1.2.3.dev0`) is rewritten to the
 * `-` form first. Only a `-` marks a pre-release, so without that rewrite the
 * letter or the dot sorts after `#` and the pre-release outranks `1.2.3`.
 * A `.post` segment is not in that list: a post-release is newer than the
 * release it follows.
 *
 * The release marker has to sort BEFORE `.`, not after it. A trailing `~`
 * (0x7E) sorts after `.` (0x2E), which ranked a shorter version above a longer
 * one whenever one was a prefix of the other (`2` above `2.1`). A pre-release
 * `-` is mapped to `!` (0x21) and a release ends in `#` (0x23), and both sit
 * before `.`:
 *
 *   `1.2.3!rc` < `1.2.3#` < `1.2.3.1#`
 *
 * Comparing the result with `<`/`>` in JS is a code-unit comparison — the same
 * ordering `COLLATE "C"` gives Postgres. Never compare these with
 * `localeCompare`, which would reintroduce exactly the locale sensitivity the
 * collation exists to rule out.
 */
export function mcpVersionSortKey(version) {
    const stripped = version
        .toLowerCase()
        .replace(/^v(?=\d)/, '')
        .replace(/\+.*/, '')
        .replace(/(\d)\.?(a|b|c|rc|alpha|beta|pre|preview|dev)(?=\d|$)/, '$1-$2');
    const marked = stripped.includes('-')
        ? stripped.replace(/-/g, '!')
        : `${stripped}#`;
    return marked.replace(/\d+/g, run => run.padStart(10, '0'));
}
/**
 * Orders two version entries best-first: installed before self-declared, then
 * by version descending.
 *
 * The source tag outranks the number because the two are not one scale — an
 * installed version was read off an artifact, a self-declared one is a claim
 * the server makes about itself, and a server can claim anything.
 */
export function compareMcpVersionEntries(a, b) {
    if (a.source !== b.source) {
        return a.source === 'installed' ? -1 : 1;
    }
    const keyA = mcpVersionSortKey(a.version);
    const keyB = mcpVersionSortKey(b.version);
    if (keyA === keyB)
        return 0;
    return keyA > keyB ? -1 : 1;
}
/**
 * The single best entry in a list, or null when the list is empty.
 *
 * An empty list is a real answer, not a missing one: a remote or never-spawned
 * server has no version in either sense.
 */
export function bestMcpVersionEntry(entries) {
    if (entries.length === 0)
        return null;
    return [...entries].sort(compareMcpVersionEntries)[0] ?? null;
}
//# sourceMappingURL=mcp-version.js.map